'use client'

/**
 * LocaleProvider — canon i18n client pour `home`.
 *
 * Home dérive la locale affichée du segment d'URL `[lang]` (conservé pour le SEO et le
 * routage). Ce provider lit le pathname via `usePathname()` pour rester synchronisé
 * avec la route active, persiste la locale dans `localStorage["yesman_locale"]` via
 * `setApiLocale`, et expose `useTranslations()` pour les chaînes de fallback du
 * dictionnaire `messages/{fr,en}.json`.
 *
 * Il ne remplace pas `lib/translations.ts` (dictionnaire UI principal) ni le hook
 * `useLanguage()` (dérivé d'URL) — les deux coexistent.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { usePathname } from 'next/navigation'
import frMessages from '@/messages/fr.json'
import {
  DEFAULT_LOCALE,
  normalizeLocale,
  resolveKey,
  setApiLocale,
  type Locale,
  type Messages,
} from '@/lib/i18n'

const messageLoaders: Record<Locale, () => Promise<Messages>> = {
  fr: () => Promise.resolve(frMessages as Messages),
  en: () => import('@/messages/en.json').then((m) => m.default as Messages),
}

/** Dérive la locale active depuis le pathname Next.js (segment `/[lang]/...`). */
function localeFromPathname(pathname: string): Locale {
  const segment = pathname.split('/')[1] ?? ''
  return normalizeLocale(segment)
}

interface LocaleContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
}

const LocaleContext = createContext<LocaleContextType>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key,
})

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE)
  const [messages, setMessages] = useState<Messages>(frMessages as Messages)

  // Synchronise la locale depuis l'URL à chaque changement de pathname.
  useEffect(() => {
    const derived = localeFromPathname(pathname)
    setLocaleState(derived)
    setApiLocale(derived)
  }, [pathname])

  // Recharge les messages à chaque changement de locale.
  useEffect(() => {
    messageLoaders[locale]().then(setMessages)
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    setApiLocale(next)
  }, [])

  const t = useCallback(
    (key: string): string => resolveKey(messages, key),
    [messages],
  )

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

/** Retourne la locale active (ex. `"fr"` ou `"en"`). */
export function useLocale(): Locale {
  return useContext(LocaleContext).locale
}

/**
 * Retourne une fonction `t()` scopée à l'espace de noms optionnel.
 *
 * Usage (analogue à Symfony `$translator->trans`) :
 *   const t = useTranslations('errors')
 *   t('generic') // → "Une erreur est survenue." | "Something went wrong."
 *
 *   const t = useTranslations()
 *   t('errors.generic') // identique, sans raccourci d'espace de noms
 */
export function useTranslations(namespace?: string) {
  const { t } = useContext(LocaleContext)
  return useCallback(
    (key: string): string => t(namespace ? `${namespace}.${key}` : key),
    [t, namespace],
  )
}

/** Expose la fonction `setLocale` pour forcer manuellement la locale. */
export function useSetLocale() {
  return useContext(LocaleContext).setLocale
}
